/**
 * client/viewmodel/viewmodel.ts — first-person weapon + hands (three.js).
 *
 * OWNER: VIEWMODEL+CAMERA agent. Sources: research/models-anims.md (assets, §4 framing), research/weapons-mp.md
 * (per-weapon fields), T5 KisakBlack cgame/cg_weapons.cpp (WeaponRunXModelAnims, StartWeaponAnim, PlayADSAnim,
 * CG_AddViewWeapon, ViewmodelRocketShouldBeAttached) and cgame/cg_camera.cpp (CG_ApplyViewAnimation).
 *
 * - One rig per viewmodel GLB (crossbow, ballistic knife, hatchet, knife). Which rig is shown =
 *   BG_GetViewmodelWeaponIndex (animmap.ts): crossbow melee shows the knife_mp viewmodel (knife only), throwing
 *   the axe shows the hatchet viewmodel, then the primary quick-raises (PM_Weapon drives that via weaponAnim).
 * - Anims come ONLY from ps.weaponAnim (id + toggle bit): any change (incl. toggle) restarts the clip with
 *   transition 0 (StartWeaponAnim(..., 0.0)); going back to IDLE lets a non-looping clip finish first (T5
 *   weapAnim 0 "idle" waits for XAnimHasFinished), and fades 0.5 s out of loops (T5 force-idle transition).
 *   Rates: animmap.ts animRate (GetWeaponAnimRate) so reload/raise/drop/sprint/melee line up with the state times.
 * - ADS: ads_up/ads_down (tag_torso only) layered with weight switch over 0.1 s and XAnimSetTime(adsFrac) /
 *   (1 - adsFrac) — T5 PlayADSAnim. Up while adsFrac rises, down while it falls.
 * - hideTags -> those bones are collapsed (zero scale). Ballistic knife blade (origin_animate_jnt, the
 *   rocketModel) is collapsed while ViewmodelRocketShouldBeAttached is false.
 * - Notetracks of the playing clip: sndnt#alias -> audio.play(alias), rmbnt#name -> input.rumble(name).
 * - Placement (view space, BG_CalculateWeaponMovement in weapmove.ts): origin/angles relative to the camera
 *   + 0.25x landing dip; the tag_camera bone (camera anims, e.g. 26 deg on the knife lunge) is applied to the
 *   world refdef (CG_ApplyViewAnimation) and cancelled on the rig so the arms stay locked to the screen (ESTIMATE).
 * - Drawn by the renderer in a second pass with the same vertical FOV as the world (ESTIMATE, models-anims §4),
 *   near plane R_ZNEAR_VIEWMODEL 0.5.
 * - Hands: only the SEAL longsleeve viewhands are built (models-anims §2); PMC hands need the asset pipeline
 *   (CONTRACT-REQUEST #V3). The rig lookup is per (weapon, body) so they drop in when built.
 */
import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { sharedTexturesPlugin } from '../assetload/sharedTextures';
import { R_ZNEAR_VIEWMODEL } from '@shared/constants';
import { angleVectors, type Vec3 } from '@shared/math';
import { EF, GameEventType, PEv, PmType, PMF, WEAPANIM_TOGGLEBIT, WeapAnim, WeaponId, type PlayerState } from '@shared/types';
import { getWeaponDef } from '@shared/weapons/defs';
import type { AudioEngine, ClientWorld, InputSource, RefDef, Viewmodel } from '../types';
import { LandDip, DamageFlinch } from '../camera/viewmove';
import { landDipFromParm } from '../camera/camera';
import {
  WEAPANIM_MASK, animRate, animStartFrac, isIdleAnim, isLoopingAnim, resolveClip, rocketVisible, viewmodelWeapon,
  findClipName, type VmAnimInfo,
} from './animmap';
import { effectivePs, frameEvents, predictedLerp, vmDebug } from './psview';
import { WeaponMovement, buildWeapMoveDef, type WeapMoveDef, type WeapMoveFrame, type WeapMovePs } from './weapmove';

/** vm_*.json shape (the fields used). */
interface VmJson extends VmAnimInfo {
  id: string;
  weapon: string;
  model: string;
  anims: string;
  hideTags: string[];
  clips: Record<string, { duration: number; looped: boolean; notetracks?: { name: string; time: number }[] }>;
}

/** WeaponId -> asset id (public/assets/anims/vm_<id>.json). */
const RIG_ASSETS: ReadonlyArray<[WeaponId, string]> = [
  [WeaponId.CROSSBOW, 'crossbow'],
  [WeaponId.KNIFE_BALLISTIC, 'ballistic_knife'],
  [WeaponId.HATCHET, 'hatchet'],
  [WeaponId.KNIFE, 'knife'],
];
/** The ballistic knife's blade bone = its rocketModel (models-anims §2 "The blade is skinned to origin_animate_jnt"). */
const ROCKET_BONE = 'origin_animate_jnt';
const COLLAPSED = 1e-5;
/** T5 PlayADSAnim blend time between ads_up and ads_down. */
const ADS_BLEND_S = 0.1;
/** T5 WeaponRunXModelAnims force-idle transition. */
const FORCE_IDLE_BLEND_S = 0.5;
/** Viewmodel light levels (ESTIMATE, tuned by eye; tools/anim/preview uses hemi 1.6 + dir 2.2). */
const VM_SUN_INTENSITY = 2.4;
const VM_AMBIENT_INTENSITY = 1.6;

/** CoD (x fwd, y left, z up) -> three (x, z, -y) as a basis matrix (== the GLBs' cod_root rotation). */
const B = new THREE.Matrix4().makeRotationX(-Math.PI / 2);
const B_INV = B.clone().invert();

interface MainAnim {
  /** ps.weaponAnim value that started it (incl. toggle bit); -1 = none. */
  anim: number;
  clip: string;
  action: THREE.AnimationAction | null;
  rate: number;
  loop: boolean;
  /** Last clip time we fired notetracks up to. */
  noteT: number;
}

class Rig {
  readonly root: THREE.Object3D;
  readonly mixer: THREE.AnimationMixer;
  readonly clips = new Map<string, THREE.AnimationClip>();
  readonly actions = new Map<string, THREE.AnimationAction>();
  readonly codRoot: THREE.Object3D;
  readonly tagCamera: THREE.Object3D | null;
  readonly rocket: THREE.Object3D | null;
  readonly moveDef: WeapMoveDef;
  adsUp: THREE.AnimationAction | null = null;
  adsDown: THREE.AnimationAction | null = null;
  adsUpW = 0;
  main: MainAnim = { anim: -1, clip: '', action: null, rate: 1, loop: false, noteT: 0 };
  pendingIdle = -1;

  constructor(readonly weapon: WeaponId, readonly info: VmJson, model: GLTF, anims: GLTF) {
    this.root = model.scene;
    this.root.matrixAutoUpdate = false;
    this.codRoot = this.root.getObjectByName('cod_root') ?? this.root;
    this.tagCamera = this.root.getObjectByName('tag_camera') ?? null;
    this.mixer = new THREE.AnimationMixer(this.root);
    for (const c of anims.animations) this.clips.set(c.name, c);
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.frustumCulled = false;
        // knife_melee (crossbow GLB composite): BO2 swaps to the knife_mp viewmodel for crossbow melee instead
        if (m.userData?.hiddenByDefault) m.visible = false;
      }
    });
    for (const tag of info.hideTags ?? []) {
      const b = this.root.getObjectByName(tag);
      if (b) b.scale.setScalar(COLLAPSED);
    }
    const def = getWeaponDef(weapon);
    this.rocket = def.reloadShowRocketTimeMs > 0 ? this.root.getObjectByName(ROCKET_BONE) ?? null : null;
    this.moveDef = buildWeapMoveDef(def);
    const up = findClipName(info, info.slots.adsUpAnim), down = findClipName(info, info.slots.adsDownAnim);
    if (up && down) {
      this.adsUp = this.action(up, true);
      this.adsDown = this.action(down, true);
    }
  }

  action(clip: string, ads = false): THREE.AnimationAction | null {
    let a = this.actions.get(clip);
    if (!a) {
      const c = this.clips.get(clip);
      if (!c) return null;
      a = this.mixer.clipAction(c);
      if (ads) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; a.timeScale = 0; a.play(); a.setEffectiveWeight(0); }
      this.actions.set(clip, a);
    }
    return a;
  }
}

export interface VmNote { name: string; clip: string; time: number }

export class FirstPersonViewmodel implements Viewmodel {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(51.08, 16 / 9, R_ZNEAR_VIEWMODEL, 1000);
  private readonly rigs = new Map<WeaponId, Rig>();
  private current: Rig | null = null;
  private audio: AudioEngine | null = null;
  private input: InputSource | null = null;
  private readonly move = new WeaponMovement();
  private readonly land = new LandDip();
  private readonly flinch = new DamageFlinch();
  private now = 0;
  private spawnCount = -1;
  private lastAdsFrac = 0;
  private adsGoingUp = false;
  private exposure = 1;
  private loadPromise: Promise<void> | null = null;
  private readonly hemi = new THREE.HemisphereLight(0xc8d4e6, 0x3a3530, 1);
  private readonly sun = new THREE.DirectionalLight(0xffffff, 1);
  private sunDirCod: Vec3 = [0.7697, 0.539, 0.342];
  private sunColor = new THREE.Color(1, 0.8, 0.6);
  // scratch
  private readonly o: Vec3 = [0, 0, 0];
  private readonly a: Vec3 = [0, 0, 0];
  private readonly fwd: Vec3 = [0, 0, 0];
  private readonly right: Vec3 = [0, 0, 0];
  private readonly up: Vec3 = [0, 0, 0];
  private readonly m1 = new THREE.Matrix4();
  private readonly m2 = new THREE.Matrix4();
  private readonly mC = new THREE.Matrix4();
  private readonly bps: WeapMovePs = {
    bobCycle: 0, pm_flags: 0, viewHeightTarget: 60, waterlevel: 0, adsFrac: 0, weaponState: 0, weaponTime: 0, speed: 0,
    velocity: [0, 0, 0], leanf: 0, viewangles: [0, 0, 0],
  };
  private readonly frame: WeapMoveFrame = { ps: this.bps, def: null as unknown as WeapMoveDef, xyspeed: 0, frametime: 0, mantleFrac: -1 };
  private flinchAds = 0;
  private readonly applyFlinch = (ang: Vec3): void => this.flinch.applyWeapon(this.now, this.flinchAds, ang);
  /** Recent notetracks (debug/tests). */
  readonly notes: VmNote[] = [];

  constructor() {
    this.camera.rotation.set(0, -Math.PI / 2, 0); // look down three +X = CoD forward (models-anims §4)
    this.scene.add(this.camera, this.hemi, this.sun, this.sun.target);
    this.sun.target.position.set(0, 0, 0);
    // read the renderer's exposure so the viewmodel lighting matches whatever the world pass uses
    this.scene.onBeforeRender = (renderer: THREE.WebGLRenderer) => {
      this.exposure = renderer.toneMapping !== THREE.NoToneMapping ? renderer.toneMappingExposure || 1 : 1;
    };
    if (typeof window !== 'undefined') {
      window.__vm = {
        debug: vmDebug,
        notes: this.notes,
        vm: this,
        clips: (w: WeaponId) => [...(this.rigs.get(w)?.clips.keys() ?? [])],
        state: () => this.debugState(),
      };
    }
  }

  load(): Promise<void> {
    this.loadPromise ??= this.loadAll();
    return this.loadPromise;
  }

  private async loadAll(): Promise<void> {
    // arms/datapad maps are one shared file for every vm_*.glb -> one Texture (assets agent, CONTRACT-REQUESTS #A1)
    const loader = new GLTFLoader().register(sharedTexturesPlugin());
    void this.loadSun();
    await Promise.all(RIG_ASSETS.map(async ([w, id]) => {
      try {
        const info = (await (await fetch(`/assets/anims/vm_${id}.json`)).json()) as VmJson;
        const [model, anims] = await Promise.all([loader.loadAsync(`/assets/${info.model}`), loader.loadAsync(`/assets/${info.anims}`)]);
        const rig = new Rig(w, info, model, anims);
        rig.root.visible = false;
        this.scene.add(rig.root);
        this.rigs.set(w, rig);
      } catch (e) {
        console.warn(`[viewmodel] failed to load vm_${id}`, e);
      }
    }));
  }

  /** Sun direction/colour from the map meta (real-time sun, map-hijacked) for the viewmodel key light. */
  private async loadSun(): Promise<void> {
    try {
      const meta = (await (await fetch('/assets/maps/mp_hijacked/meta.json')).json()) as { lighting?: { sun?: { codDir?: number[]; color?: number[] } } };
      const s = meta.lighting?.sun;
      if (s?.codDir?.length === 3) this.sunDirCod = [s.codDir[0], s.codDir[1], s.codDir[2]];
      if (s?.color?.length === 3) {
        const m = Math.max(s.color[0], s.color[1], s.color[2]) || 1;
        this.sunColor = new THREE.Color(s.color[0] / m, s.color[1] / m, s.color[2] / m);
      }
    } catch { /* keep defaults */ }
  }

  bindFeedback(audio: AudioEngine, input: InputSource): void {
    this.audio = audio;
    this.input = input;
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    for (const r of this.rigs.values()) { r.mixer.stopAllAction(); this.scene.remove(r.root); }
    this.rigs.clear();
  }

  update(world: ClientWorld, refdef: RefDef, dtMs: number): void {
    if (this.camera.fov !== refdef.viewmodelFovY) { this.camera.fov = refdef.viewmodelFovY; this.camera.updateProjectionMatrix(); }
    const dt = vmDebug.paused ? 0 : Math.max(0, dtMs) / 1000;
    this.now += dt * 1000;
    const ps = effectivePs(world);
    this.events(world, ps);

    if (ps.spawnCount !== this.spawnCount) {
      this.spawnCount = ps.spawnCount;
      this.move.reset();
      for (const r of this.rigs.values()) { r.main.anim = -1; r.pendingIdle = -1; }
    }

    const hidden = ps.pm_type === PmType.DEAD || ps.pm_type === PmType.DEAD_LINKED || ps.pm_type === PmType.SPECTATOR
      || ps.pm_type === PmType.INTERMISSION || (ps.eFlags & EF.DEAD) !== 0 || ps.weapon === WeaponId.NONE || !world.connected;
    const vmW = viewmodelWeapon(ps, (w) => w !== WeaponId.NONE && getWeaponDef(w).useAsMelee);
    const rig = hidden ? null : this.rigs.get(vmW) ?? null;
    let rigChanged = false;
    // T5 "newPlayerstate" (resume mid-state) only when the viewmodel (re)appears, not on weapon/rig switches
    const resume = this.current === null;
    if (rig !== this.current) {
      if (this.current) { this.current.root.visible = false; this.current.mixer.stopAllAction(); this.current.main.anim = -1; this.current.main.action = null; }
      this.current = rig;
      rigChanged = true;
      if (rig) {
        rig.root.visible = true;
        rig.main.anim = -1;
        if (rig.adsUp && rig.adsDown) { rig.adsUp.play(); rig.adsDown.play(); }
        this.move.resetFlag = true;
      }
    }
    if (!rig) return;

    this.animate(rig, ps, dt, rigChanged, resume && rigChanged);
    this.place(rig, world, ps, refdef, dt);
    this.light(refdef);
  }

  // ---------------------------------------------------------------------------------------------
  private events(world: ClientWorld, ps: PlayerState): void {
    for (const e of frameEvents(world)) {
      if (e.src === 'pred') {
        if (e.ev === PEv.WEAPON_FIRE || e.ev === PEv.WEAPON_FIRE_LAST) {
          const w = (e.parm & 0xff) as WeaponId;
          if (w !== WeaponId.NONE) {
            const md = this.rigs.get(w)?.moveDef ?? buildWeapMoveDef(getWeaponDef(w));
            this.move.fire(md, ps.adsFrac, Math.random(), Math.random());
          }
        } else if (e.ev === PEv.LAND) {
          this.land.land(this.now, landDipFromParm(e.parm));
        }
      } else if (e.src === 'game' && e.ge.type === GameEventType.DAMAGE_TAKEN) {
        // gun flinch (BG_ComputeAndApplyWeaponMovement_DamageKickAngles); direction from the attacker like the camera
        const ge = e.ge;
        const at = ge.attacker !== world.localClientNum ? world.getPlayer(ge.attacker) : undefined;
        let dir: Vec3 | null = null;
        if (at) {
          const d: Vec3 = [world.view.origin[0] - at.origin[0], world.view.origin[1] - at.origin[1], world.view.origin[2] - at.origin[2]];
          const l = Math.hypot(d[0], d[1], d[2]);
          if (l > 0.001) dir = [d[0] / l, d[1] / l, d[2] / l];
        }
        angleVectors(world.view.viewangles, this.fwd, this.right, null);
        this.flinch.hit(this.now, ge.amount, ps.maxHealth || 100, dir, this.fwd, this.right);
      }
    }
  }

  // ---------------------------------------------------------------------------------------------
  private animate(rig: Rig, ps: PlayerState, dt: number, rigChanged: boolean, resume: boolean): void {
    const def = getWeaponDef(rig.weapon);
    const want = ps.weaponAnim & (WEAPANIM_MASK | WEAPANIM_TOGGLEBIT);
    const clipEmpty = (ps.ammoClip[ps.weapon] ?? 0) === 0;
    const main = rig.main;

    if (want !== main.anim || rigChanged) {
      const wantIdle = isIdleAnim(want);
      if (wantIdle && !rigChanged && main.action && !main.loop && !this.finished(main)) {
        rig.pendingIdle = want; // let the current one-shot finish (T5 weapAnim "idle")
      } else {
        const fade = wantIdle && !rigChanged && main.action && main.loop && !isIdleAnim(main.anim) ? FORCE_IDLE_BLEND_S : 0;
        this.start(rig, def, ps, want, clipEmpty, resume, fade);
      }
      main.anim = want;
    }
    if (rig.pendingIdle >= 0) {
      if (!isIdleAnim(want)) rig.pendingIdle = -1;
      else if (this.finished(main)) { this.start(rig, def, ps, rig.pendingIdle, clipEmpty, false, 0); main.anim = want; rig.pendingIdle = -1; }
    }

    // ADS layer (PlayADSAnim)
    if (rig.adsUp && rig.adsDown) {
      let upNow: boolean;
      if (def.aimDownSight) {
        if (ps.adsFrac > this.lastAdsFrac) this.adsGoingUp = true;
        else if (ps.adsFrac < this.lastAdsFrac) this.adsGoingUp = false;
        else if (ps.adsFrac >= 1) this.adsGoingUp = true;
        else if (ps.adsFrac <= 0) this.adsGoingUp = false;
        upNow = this.adsGoingUp;
      } else upNow = false; // non-ADS weapon: ads_down at time 1 = hip pose
      const frac = def.aimDownSight ? ps.adsFrac : 0;
      const target = upNow ? 1 : 0;
      if (rigChanged) rig.adsUpW = target;
      const step = dt / ADS_BLEND_S;
      rig.adsUpW = rig.adsUpW < target ? Math.min(target, rig.adsUpW + step) : Math.max(target, rig.adsUpW - step);
      rig.adsUp.setEffectiveWeight(rig.adsUpW);
      rig.adsDown.setEffectiveWeight(1 - rig.adsUpW);
      const du = rig.adsUp.getClip().duration, dd = rig.adsDown.getClip().duration;
      rig.adsUp.time = Math.min(du - 1e-4, Math.max(0, frac * du));
      rig.adsDown.time = Math.min(dd - 1e-4, Math.max(0, (1 - frac) * dd));
    }
    this.lastAdsFrac = ps.adsFrac;

    // advance + notetracks
    const before = main.action ? main.action.time : 0;
    rig.mixer.update(dt);
    if (main.action) {
      const after = main.action.time;
      const dur = main.action.getClip().duration;
      if (main.loop && after < before - 1e-6) { this.notesBetween(rig, main.noteT, dur + 1); this.notesBetween(rig, -1, after); }
      else this.notesBetween(rig, main.noteT, after);
      main.noteT = after;
    }

    // ballistic knife blade
    if (rig.rocket) rig.rocket.scale.setScalar(rocketVisible(def, ps) ? 1 : COLLAPSED);
  }

  private finished(m: MainAnim): boolean {
    const a = m.action;
    if (!a) return true;
    if (m.rate <= 0) return true;
    return a.paused || !a.enabled || a.time >= a.getClip().duration - 1e-3;
  }

  private start(rig: Rig, def: ReturnType<typeof getWeaponDef>, ps: PlayerState, anim: number, clipEmpty: boolean, newPs: boolean, fade: number): void {
    const r = resolveClip(rig.info, anim, clipEmpty);
    const main = rig.main;
    const prev = main.action;
    if (!r) { if (prev) prev.stop(); main.action = null; return; }
    const action = rig.action(r.clip);
    if (!action) { main.action = null; return; }
    const clip = action.getClip();
    const rate = animRate(def, r.anim, clip.duration);
    const loop = isLoopingAnim(r.anim) || (rig.info.clips[r.clip]?.looped ?? false);
    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !loop;
    action.timeScale = rate;
    action.setEffectiveWeight(1);
    const startT = newPs ? animStartFrac(def, r.anim, ps.weaponTime) * clip.duration : 0;
    action.time = Math.min(startT, Math.max(0, clip.duration - 1e-4));
    action.play();
    stopOtherActions(rig.actions.values(), action, rig.adsUp, rig.adsDown, fade > 0 ? prev : null);
    if (fade > 0 && prev && prev !== action) action.crossFadeFrom(prev, fade, false);
    main.clip = r.clip;
    main.action = action;
    main.rate = rate;
    main.loop = loop;
    main.noteT = action.time - 1e-6; // notes at the start frame fire (e.g. melee_knife_stab @0)
  }

  private notesBetween(rig: Rig, t0: number, t1: number): void {
    const notes = rig.info.clips[rig.main.clip]?.notetracks;
    if (!notes || t1 <= t0) return;
    for (const n of notes) {
      if (n.time > t0 && n.time <= t1) this.notetrack(rig.main.clip, n.name, n.time);
    }
  }

  /** ProcessWeaponNoteTracks: sndnt#alias -> sound, rmbnt#name -> rumble (models-anims §1 "Notetracks"). */
  private notetrack(clip: string, name: string, time: number): void {
    this.notes.push({ name, clip, time });
    if (this.notes.length > 64) this.notes.shift();
    if (name.startsWith('sndnt#')) this.audio?.play(name.slice(6));
    else if (name.startsWith('rmbnt#')) this.input?.rumble(name.slice(6));
  }

  // ---------------------------------------------------------------------------------------------
  /** CG_AddViewWeapon placement in view space + CG_ApplyViewAnimation on the refdef. */
  private place(rig: Rig, world: ClientWorld, ps: PlayerState, refdef: RefDef, dt: number): void {
    const v = world.view;
    const vel = predictedLerp.velocity; // render-rate lerp of the predicted velocity / bobCycle (camera updates it)
    const xyspeed = ps.pm_flags & PMF.LADDER ? Math.abs(vel[2]) : Math.hypot(vel[0], vel[1]);
    const mantleFrac = ps.pm_flags & PMF.MANTLE ? 1 : -1; // ESTIMATE: Mantle_GetMantleFrac needs the mantle anim length
    const bps = this.bps;
    bps.bobCycle = predictedLerp.bobCycle; bps.pm_flags = ps.pm_flags; bps.viewHeightTarget = ps.viewHeightTarget; bps.waterlevel = ps.waterlevel;
    bps.adsFrac = ps.adsFrac; bps.weaponState = ps.weaponState; bps.weaponTime = ps.weaponTime; bps.speed = ps.speed;
    bps.velocity = vel; bps.leanf = ps.leanf; bps.viewangles = v.viewangles;
    this.flinchAds = ps.adsFrac;
    const o = this.o, a = this.a;
    const fr = this.frame;
    fr.ps = bps; fr.def = rig.moveDef; fr.xyspeed = xyspeed; fr.frametime = dt; fr.mantleFrac = mantleFrac;
    this.move.calculate(fr, o, a, this.applyFlinch);
    // landing (world z, 0.25x) expressed in view-local coords
    const lz = this.land.gun(this.now);
    if (lz !== 0) {
      angleVectors(refdef.angles, this.fwd, this.right, this.up);
      o[0] += lz * this.fwd[2]; o[1] += lz * -this.right[2]; o[2] += lz * this.up[2];
    }
    // tag_camera relative to the rig root (C) from the anim pose
    rig.root.matrix.identity();
    rig.root.updateMatrixWorld(true);
    const C = this.mC.identity();
    if (rig.tagCamera) {
      this.m1.copy(rig.codRoot.matrixWorld).invert();
      C.multiplyMatrices(this.m1, rig.tagCamera.matrixWorld); // CoD space (below cod_root)
    }
    // M_cod = T(o) * R(angles) * C^-1 ; root = B * M_cod * B^-1
    const M = this.m2;
    codAnglesMatrix(M, a);
    M.setPosition(o[0], o[1], o[2]);
    this.m1.copy(C).invert();
    M.multiply(this.m1);
    rig.root.matrix.multiplyMatrices(B, M).multiply(B_INV);
    rig.root.updateMatrixWorld(true);
    applyCameraAnim(refdef, C);
    vmDebug.stats.refdef = { origin: [...refdef.origin], angles: [...refdef.angles] };
  }

  private light(refdef: RefDef): void {
    angleVectors(refdef.angles, this.fwd, this.right, this.up);
    const d = this.sunDirCod;
    const lx = d[0] * this.fwd[0] + d[1] * this.fwd[1] + d[2] * this.fwd[2];
    const ly = -(d[0] * this.right[0] + d[1] * this.right[1] + d[2] * this.right[2]); // left
    const lz = d[0] * this.up[0] + d[1] * this.up[1] + d[2] * this.up[2];
    this.sun.position.set(lx * 100, lz * 100, -ly * 100); // CoD view-local -> three view space
    // ESTIMATE: hands/gun lit by a sky/ground ambient + the map sun (three built-in materials, drawn straight to
    // the canvas after the world's post pass; levels matched by eye to the world and extracted/previews).
    const k = this.exposure > 0 ? 1 / this.exposure : 1; // 1 unless someone enables three tone mapping
    this.sun.color.copy(this.sunColor);
    this.sun.intensity = VM_SUN_INTENSITY * k;
    this.hemi.intensity = VM_AMBIENT_INTENSITY * k;
    // the hemisphere "up" must follow the world up in view space
    this.hemi.position.set(this.fwd[2], this.up[2], this.right[2]);
  }

  private debugState(): Record<string, unknown> {
    const r = this.current;
    return {
      loaded: [...this.rigs.keys()],
      rig: r?.weapon ?? null,
      clip: r?.main.clip ?? null,
      time: r?.main.action?.time ?? null,
      rate: r?.main.rate ?? null,
      pendingIdle: r?.pendingIdle ?? null,
      adsUpW: r?.adsUpW ?? null,
      baseOrigin: [...this.move.baseOrigin],
      recoil: [...this.move.recoilAngles],
      origin: [...this.o],
      angles: [...this.a],
      rocket: r?.rocket ? r.rocket.scale.x > 0.5 : null,
    };
  }
}

/**
 * T5 StartWeaponAnim replaces the main anim outright, so every other main-slot action must leave the mixer.
 * Uses isScheduled(), not isRunning(): a finished LoopOnce clip with clampWhenFinished is `paused` and a rate-0
 * clip has timeScale 0, so isRunning() is false for both although they still hold weight 1 in the mixer. Leaving
 * them blended in kept e.g. the last frame of sprint_out mixed 50/50 into idle and ADS (verify-look finding).
 */
export function stopOtherActions(actions: Iterable<THREE.AnimationAction>, ...keep: (THREE.AnimationAction | null)[]): void {
  for (const a of actions) {
    if (keep.includes(a)) continue;
    if (a.isScheduled()) a.stop();
  }
}

/** Rotation matrix whose columns are the IW AnglesToAxis rows (fwd, left, up) — CoD space. */
export function codAnglesMatrix(out: THREE.Matrix4, angles: Readonly<Vec3>): THREE.Matrix4 {
  const d = Math.PI / 180;
  const sy = Math.sin(angles[1] * d), cy = Math.cos(angles[1] * d);
  const sp = Math.sin(angles[0] * d), cp = Math.cos(angles[0] * d);
  const sr = Math.sin(angles[2] * d), cr = Math.cos(angles[2] * d);
  const fx = cp * cy, fy = cp * sy, fz = -sp;
  const lx = -(-sr * sp * cy + cr * sy), ly = -(-sr * sp * sy - cr * cy), lz = sr * cp;
  const ux = cr * sp * cy + sr * sy, uy = cr * sp * sy - sr * cy, uz = cr * cp;
  return out.set(
    fx, lx, ux, 0,
    fy, ly, uy, 0,
    fz, lz, uz, 0,
    0, 0, 0, 1,
  );
}

const _vm = new THREE.Matrix4();
const _q = new THREE.Vector3();
/**
 * CG_ApplyViewAnimation: refdef = refdef * tag_camera (CoD space). Skipped when C is the identity (no camera anim).
 */
export function applyCameraAnim(refdef: RefDef, C: THREE.Matrix4): void {
  const e = C.elements;
  if (Math.abs(e[0] - 1) < 1e-6 && Math.abs(e[5] - 1) < 1e-6 && Math.abs(e[10] - 1) < 1e-6 && Math.abs(e[12]) < 1e-6 && Math.abs(e[13]) < 1e-6 && Math.abs(e[14]) < 1e-6) return;
  codAnglesMatrix(_vm, refdef.angles);
  _vm.setPosition(refdef.origin[0], refdef.origin[1], refdef.origin[2]);
  _vm.multiply(C);
  const m = _vm.elements;
  refdef.origin[0] = m[12]; refdef.origin[1] = m[13]; refdef.origin[2] = m[14];
  axisToAngles(refdef.angles, m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]);
  void _q;
}

/** IW AxisToAngles from fwd (fx,fy,fz), left (lx,ly,lz), up (ux,uy,uz). */
export function axisToAngles(out: Vec3, fx: number, fy: number, fz: number, lx: number, ly: number, lz: number, _ux: number, _uy: number, uz: number): void {
  const r2d = 180 / Math.PI;
  const yaw = Math.atan2(fy, fx) * r2d;
  const horiz = Math.hypot(fx, fy);
  const pitch = -Math.atan2(fz, horiz) * r2d;
  // roll from the left vector's z and the up vector's z (Q3 AxisToAngles style)
  const roll = Math.atan2(-lz, uz) * r2d;
  out[0] = pitch; out[1] = yaw; out[2] = -roll;
}
